import React, { memo, useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DragDropContext, DropResult } from "react-beautiful-dnd";

import {
  getActiveMissionByVehicleId,
  getQueueManagerMissionsData,
  getMissionsPageFleetId,
} from "Slices/missionsPage/selectors";
import { getVehiclesDict } from "Slices/vehicles/selectors";
import { MissionsPageSearchContext } from "Components/MissionsPage/SearchContextHelper";
import {
  reorderMissions,
  moveMissionToVehicle,
  reorderFleetQueue,
  fetchMissions,
  UNASSIGNED_MISSIONS_KEY,
} from "Slices/missionsPage";

import { MissionsByVehicleSection } from "../MissionsByVehicleSection/VehicleSection";
import { filterMissions } from "Components/QueueManagerTable/utils";
import { QueueManagerMissionsData } from "Components/QueueManagerTable/types";

import { QueueManagerTableBodyStyled } from "./styles";

export const QueueManagerTableBody = memo(() => {
  const dispatch = useDispatch();
  const queueManagerMissionsData = useSelector(getQueueManagerMissionsData);
  const vehiclesDict = useSelector(getVehiclesDict);
  const missionsPageFleetId = useSelector(getMissionsPageFleetId);
  const [data, setData] = useState<QueueManagerMissionsData>(
    queueManagerMissionsData
  );
  const activeMissionByVehicleId = useSelector(getActiveMissionByVehicleId);

  const { searchValue } = useContext(MissionsPageSearchContext);

  useEffect(() => {
    const filteredData = filterMissions(queueManagerMissionsData, searchValue);
    setData(filteredData);
  }, [queueManagerMissionsData, searchValue]);

  const onDragEnd = (result: DropResult): void => {
    if (!result.destination) return;

    const { source, destination } = result;

    // No movement — skip
    if (
      source.droppableId === destination.droppableId &&
      source.index === destination.index
    ) {
      return;
    }

    const sourceItems = [...data[source.droppableId]];
    const [removed] = sourceItems.splice(source.index, 1);

    // Clamp drop index so the hot/non-hot boundary is respected.
    // Server sorts by (-priority, order): hot jobs always lead, non-hot follow.
    // - Non-hot mission: cannot go above hot jobs  → clamp index UP   to hotCount
    // - Hot mission:     cannot go below non-hot   → clamp index DOWN to hotCount
    const clampIndex = (idx: number, items: typeof sourceItems) => {
      const hotCount = items.filter((m) => m.isHot).length;
      return removed.isHot ? Math.min(idx, hotCount) : Math.max(idx, hotCount);
    };

    if (source.droppableId === destination.droppableId) {
      // ── Within-section reorder ──────────────────────────────────────────────
      const effectiveIndex = clampIndex(destination.index, sourceItems);
      sourceItems.splice(effectiveIndex, 0, removed);

      const previousIds = data[source.droppableId].map((m) => m.id);
      const newIds = sourceItems.map((m) => m.id);

      setData({ ...data, [source.droppableId]: sourceItems });
      dispatch(reorderMissions(newIds) as any);
      (
        dispatch(
          reorderFleetQueue({
            missionId: removed.id,
            newIndex: effectiveIndex,
          }) as any
        ).unwrap() as Promise<any>
      ).catch(() => {
        dispatch(reorderMissions(previousIds) as any);
      });
    } else {
      // ── Cross-section vehicle reassignment ──────────────────────────────────
      const destItems = [...(data[destination.droppableId] ?? [])];
      const effectiveIndex = clampIndex(destination.index, destItems);
      destItems.splice(effectiveIndex, 0, removed);

      const newSourceIds = sourceItems.map((m) => m.id);
      const newDestIds = destItems.map((m) => m.id);
      const previousSourceIds = data[source.droppableId].map((m) => m.id);
      const previousDestIds = (data[destination.droppableId] ?? []).map(
        (m) => m.id
      );
      const previousVehicleId = removed.vehicle ?? null;
      const newVehicleId =
        destination.droppableId !== UNASSIGNED_MISSIONS_KEY
          ? destination.droppableId
          : null;

      // Optimistically update Redux with both the vehicle change and the new
      // positions. All three dispatches are batched into one render by React 18,
      // so getQueueManagerMissionsData immediately reflects the new state and
      // the useEffect syncs local state without any additional guards.
      dispatch(
        moveMissionToVehicle({ missionId: removed.id, newVehicleId }) as any
      );
      dispatch(reorderMissions(newDestIds) as any);
      dispatch(reorderMissions(newSourceIds) as any);
      setData({
        ...data,
        [source.droppableId]: sourceItems,
        [destination.droppableId]: destItems,
      });

      (
        dispatch(
          reorderFleetQueue({
            missionId: removed.id,
            newIndex: effectiveIndex,
            newVehicleId: newVehicleId ?? undefined,
          }) as any
        ).unwrap() as Promise<any>
      )
        .then(() => {
          // Re-fetch to confirm the server-assigned position
          if (missionsPageFleetId) {
            dispatch(
              fetchMissions({ currentFleetId: missionsPageFleetId }) as any
            );
          }
        })
        .catch(() => {
          // Rollback: revert vehicle, then both section orderings
          dispatch(
            moveMissionToVehicle({
              missionId: removed.id,
              newVehicleId: previousVehicleId,
            }) as any
          );
          dispatch(reorderMissions(previousDestIds) as any);
          dispatch(reorderMissions(previousSourceIds) as any);
        });
    }
  };

  return (
    <QueueManagerTableBodyStyled>
      <DragDropContext onDragEnd={(result) => onDragEnd(result)}>
        {Object.entries(data).map(([vehicleId, missions]) => {
          if (vehicleId === UNASSIGNED_MISSIONS_KEY) {
            return (
              <MissionsByVehicleSection
                key={vehicleId}
                vehicleId={vehicleId}
                vehicleName={"Fleet Unassigned"}
                missions={missions}
              />
            );
          }
          const activeMission = activeMissionByVehicleId[vehicleId];
          return (
            <MissionsByVehicleSection
              key={vehicleId}
              vehicleId={vehicleId}
              vehicleName={vehiclesDict[vehicleId]?.name ?? "..."}
              missions={missions}
              activeMission={activeMission}
            />
          );
        })}
      </DragDropContext>
    </QueueManagerTableBodyStyled>
  );
});
